import { TwilioPhoneNumberModel } from "@jugl-web/rest-api";
import { isTwilioApiError, useToast, useTranslations } from "@jugl-web/utils";
import { useCallback, useState } from "react";
import { useTwilioAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/TwilioAccountProvider/useTwilioAccount";

const TWILIO_ACCOUNT_ALREADY_EXISTS_ERROR = "account_already_exists";

interface UseTwilioPhoneNumberAssociationFlowOptions {
  associatePhoneNumber: (
    phoneNumber: TwilioPhoneNumberModel
  ) => Promise<unknown>;
  successMessage: string;
}

export const useTwilioPhoneNumberAssociationFlow = ({
  associatePhoneNumber,
  successMessage,
}: UseTwilioPhoneNumberAssociationFlowOptions) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { listPhoneNumbers, isListingPhoneNumbers } = useTwilioAccount();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<TwilioPhoneNumberModel[]>(
    []
  );

  const startFlow = useCallback(
    async (credentials: { sid: string; token: string | null }) => {
      setPhoneNumbers([]);
      setIsDialogOpen(true);
      try {
        setPhoneNumbers(await listPhoneNumbers(credentials));
      } catch {
        setIsDialogOpen(false);
        toast(
          t({
            id: "client-support-page.twilio-list-phone-numbers-failed",
            defaultMessage:
              "Couldn't fetch phone numbers. Please check your credentials and try again",
          }),
          { variant: "error" }
        );
      }
    },
    [listPhoneNumbers, t, toast]
  );

  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  const associate = useCallback(
    async (phoneNumber: TwilioPhoneNumberModel) => {
      try {
        await associatePhoneNumber(phoneNumber);
        setIsDialogOpen(false);
        toast(successMessage);
      } catch (error) {
        if (
          isTwilioApiError(error) &&
          error.data.errors.length > 0 &&
          error.data.errors[0].message === TWILIO_ACCOUNT_ALREADY_EXISTS_ERROR
        ) {
          toast(
            t({
              id: "client-support-page.twilio-phone-number-already-exists",
              defaultMessage:
                "Selected Twilio account is already associated to another Jugl Workspace",
            }),
            { variant: "error" }
          );
          return;
        }
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [associatePhoneNumber, successMessage, t, toast]
  );

  return {
    isDialogOpen,
    isListingPhoneNumbers,
    phoneNumbers,
    startFlow,
    closeDialog,
    associate,
  };
};
